import { useEffect, useSyncExternalStore } from 'react';
import { preferencesApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import { dateInZone } from '../lib/personTime';
import type { TodayPlanView } from '../types/models';

// ---------------------------------------------------------------------------
// What "/" needs to know before it can choose a screen (web app v4 Phase 4):
// whether the person is trying the new Today, the day they last read their
// morning brief, their time zone (whose date "today" is), and whether Today
// opens on the timeline or the list. All are
// server preferences, so they follow the person from browser to browser
// (the brief's read state used to be `cadence_brief_dismissed_date` in one
// browser). Read once per sign-in and shared, so going home doesn't wait on
// a request each time; changes are written through and shown at once.
// ---------------------------------------------------------------------------

export interface TodayPrefs {
  /** Trying the new Today instead of the dashboard. */
  tryNewToday: boolean;
  /** The person's date they last read the morning brief on (yyyy-MM-dd), or null. */
  briefFoldedOn: string | null;
  /** The person's IANA time zone (Settings → Your day), or null if unknown. */
  timeZone: string | null;
  /** End of their working day, "HH:mm[:ss]", or null if unknown. */
  workDayEnd: string | null;
  /** How Today shows the plan when it opens; null until they choose (the timeline). */
  planView: TodayPlanView | null;
}

export interface TodayPrefsState {
  /** Whose preferences these are; a different person signing in reads again. */
  userId: string | null;
  status: 'idle' | 'loading' | 'ready';
  /** The read failed: the defaults below stand in (the dashboard, brief not shown). */
  failed: boolean;
  prefs: TodayPrefs;
}

const DEFAULTS: TodayPrefs = { tryNewToday: false, briefFoldedOn: null, timeZone: null, workDayEnd: null, planView: null };
const EMPTY: TodayPrefsState = { userId: null, status: 'idle', failed: false, prefs: DEFAULTS };

let state: TodayPrefsState = EMPTY;
let readSeq = 0;
const listeners = new Set<() => void>();
// What this tab changed, and when: a read that started before a change mustn't
// put the old value back when it lands (nor be thrown away, or "/" would wait
// for it for ever).
let writeEpoch = 0;
let lastWrite: { [K in keyof TodayPrefs]?: { epoch: number; value: TodayPrefs[K] } } = {};

function setLocally<K extends keyof TodayPrefs>(key: K, value: TodayPrefs[K]) {
  writeEpoch++;
  lastWrite = { ...lastWrite, [key]: { epoch: writeEpoch, value } };
  set({ ...state, prefs: { ...state.prefs, [key]: value } });
}

function set(next: TodayPrefsState) {
  state = next;
  listeners.forEach(l => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Read the person's preferences (again, with `force`). Never throws: a failed read leaves the defaults. */
export async function loadTodayPrefs(userId: string, force = false): Promise<void> {
  if (!force && state.userId === userId && state.status !== 'idle') return;
  const seq = ++readSeq;
  const epochAtStart = writeEpoch;
  // Someone else's changes don't carry over (changes made before anyone's
  // preferences were read are this person's: keep them).
  if (state.userId !== null && state.userId !== userId) lastWrite = {};
  const keep = state.userId === userId ? state.prefs : DEFAULTS;
  // A re-read keeps what's on screen; only a first read shows as loading.
  set({ userId, status: state.userId === userId && state.status === 'ready' ? 'ready' : 'loading', failed: false, prefs: keep });
  try {
    const p = await preferencesApi.get();
    if (seq !== readSeq) return;
    const read: TodayPrefs = {
      tryNewToday: !!p?.tryNewToday,
      briefFoldedOn: p?.briefFoldedOn ?? null,
      timeZone: p?.briefingEmailTimezone ?? null,
      workDayEnd: p?.workDayEnd ?? null,
      planView: p?.todayPlanView === 'timeline' || p?.todayPlanView === 'list' ? p.todayPlanView : null,
    };
    set({ userId, status: 'ready', failed: false, prefs: withLocalChanges(read, epochAtStart) });
  } catch {
    if (seq !== readSeq) return;
    set({ userId, status: 'ready', failed: true, prefs: withLocalChanges(keep, epochAtStart) });
  }
}

/** What a read found, with anything this tab changed since the read began. */
function withLocalChanges(read: TodayPrefs, since: number): TodayPrefs {
  const out: TodayPrefs = { ...read };
  const put = <K extends keyof TodayPrefs>(key: K) => {
    const w = lastWrite[key];
    if (w && w.epoch > since) out[key] = w.value as TodayPrefs[K];
  };
  put('tryNewToday');
  put('briefFoldedOn');
  put('planView');
  return out;
}

/**
 * Try the new Today, or go back to the dashboard. Shown at once; if the save
 * fails it's put back and the error is thrown for the caller to say so.
 */
export async function setTryNewToday(on: boolean): Promise<void> {
  const before = state.prefs.tryNewToday;
  const whose = state.userId;
  setLocally('tryNewToday', on);
  try {
    await preferencesApi.update({ tryNewToday: on });
  } catch (e) {
    // Only put it back for the same person (someone else may have signed in since).
    if (state.userId === whose) setLocally('tryNewToday', before);
    throw e;
  }
}

/**
 * The brief for `date` (the person's yyyy-MM-dd) has been read: it folds to one
 * line in every browser. Shown at once and kept for this tab even if the save
 * fails (so going home can't send the person straight back to the brief); the
 * error is thrown for the caller to mention.
 */
export async function foldBriefOn(date: string): Promise<void> {
  setLocally('briefFoldedOn', date);
  await preferencesApi.update({ briefFoldedOn: date });
}

/**
 * Today opens on `view` from now on, in every browser. Shown at once; if the
 * save fails the error is thrown for the caller to mention, and the next read
 * of the preferences may put the saved choice back. Choosing it again retries.
 */
export async function setTodayPlanView(view: TodayPlanView): Promise<void> {
  setLocally('planView', view);
  await preferencesApi.update({ todayPlanView: view });
}

/** The person's date today in their own zone (the browser's while the zone is unknown). */
export function personToday(now: Date = new Date()): string {
  return dateInZone(now, state.prefs.timeZone);
}

/** The shared state, read for the signed-in person (and read once if it hasn't been). */
export function useTodayPrefs(): TodayPrefsState {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const snapshot = useSyncExternalStore(subscribe, () => state, () => state);
  useEffect(() => {
    if (userId) void loadTodayPrefs(userId);
  }, [userId]);
  // Another person's answers (or none yet) count as still loading.
  if (!userId || snapshot.userId !== userId || snapshot.status === 'idle') {
    return { ...EMPTY, userId, status: userId ? 'loading' : 'idle' };
  }
  return snapshot;
}

/**
 * Whether "/" shows the new Today: always for an assistant (their old home was
 * Executives, which stays at /assistant), never for a platform admin, and for
 * everyone else while they try it (`'loading'` until their preference is read).
 */
export function useNewToday(): boolean | 'loading' {
  const { user } = useAuth();
  const prefs = useTodayPrefs();
  const role = (user?.role ?? '').trim().toLowerCase();
  if (role === 'executiveassistant') return true;
  if (role === 'platformadmin') return false;
  if (prefs.status !== 'ready') return 'loading';
  return prefs.prefs.tryNewToday;
}

/** For tests: forget everything read. */
export function resetTodayPrefsForTests(): void {
  readSeq++;
  writeEpoch = 0;
  lastWrite = {};
  state = EMPTY;
  listeners.forEach(l => l());
}
